import { useState, type ReactNode } from 'react';
import { COLORS } from '../lib/theme';
import { Icon, type IconName } from './Icon';
import { ThemeToggle } from './ThemeToggle';
import { AnimatedNumber } from './AnimatedNumber';

export type NavItem = {
  id: string;
  label: string;
  icon: IconName;
  /** Optional when the item is purely a parent group (has children). */
  href?: string;
  badge?: number;
  /** Nested items — when present the row becomes an expandable group. */
  children?: NavItem[];
  /**
   * An absolute URL into another app. Rendered as a plain anchor: the browser
   * navigates, the router never sees the click, and the row is never shown as
   * active — the current path belongs to this app, not to the one the link
   * leaves for.
   */
  external?: boolean;
  /**
   * A "coming soon" row: listed so the menu matches the web shop's, but inert.
   * Rendered as a plain div — neither focusable nor announced as a link — with
   * `disabledTitle` as its tooltip.
   */
  disabled?: boolean;
  disabledTitle?: string;
};

export type NavSection = {
  label?: string;
  items: NavItem[];
};

const PRIMARY: NavItem[] = [
  { id: "overview", label: "Overview", icon: "dashboard", href: "/" },
  { id: "hunts",    label: "Hunts",    icon: "hunts",     href: "/hunts",   badge: 5 },
  { id: "teams",    label: "Teams",    icon: "group",     href: "/teams",   badge: 3 },
];

const ACCOUNT: NavItem[] = [
  { id: "settings", label: "Settings", icon: "settings", href: "/account/settings" },
];

const DEFAULT_NAV_SECTIONS: NavSection[] = [
  { items: PRIMARY },
  { label: 'Account', items: ACCOUNT },
];

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href + "/");
}

function isItemActive(pathname: string, item: NavItem): boolean {
  if (item.external) return false;
  if (item.href && isActive(pathname, item.href)) return true;
  return (item.children ?? []).some((child) => isItemActive(pathname, child));
}

function rowStyle({ active, depth }: { active: boolean; depth: number }) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 12,
    height: 40,
    padding: `0 12px 0 ${12 + depth * 16}px`,
    borderRadius: 6,
    background: active ? COLORS.secondaryFixedDim : "transparent",
    // Pair the active pill with its fixed on-colour: `secondaryFixedDim` stays
    // light in both themes, so the text must be the dark `onSecondaryFixed`
    // (also fixed) — not `primary`, which flips to light-green on dark and
    // leaves pale-on-white text.
    color: active ? COLORS.onSecondaryFixed : COLORS.onSurface,
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    position: "relative" as const,
    cursor: "pointer" as const,
    border: "none",
    width: "100%",
    textAlign: "left" as const,
    textDecoration: "none",
  };
}

function ActiveStripe() {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        background: COLORS.primary,
      }}
    />
  );
}

function Badge({ value, active, id }: { value: number; active: boolean; id?: string }) {
  return (
    <span
      style={{
        minWidth: 22,
        height: 22,
        padding: "0 6px",
        borderRadius: 999,
        background: active ? COLORS.primary : "var(--nl-primary-opacity-12)",
        color: active ? COLORS.onPrimary : COLORS.primary,
        fontSize: 11,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AnimatedNumber value={value} id={id} />
    </span>
  );
}

function SidebarRow({
  item,
  pathname,
  depth = 0,
  onClick,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  depth?: number;
  onClick?: () => void;
  onNavigate?: (href: string) => void;
}) {
  const hasChildren = (item.children?.length ?? 0) > 0;
  const directActive = !item.external && item.href ? isActive(pathname, item.href) : false;
  const branchActive = isItemActive(pathname, item);
  const [open, setOpen] = useState(branchActive);

  if (item.disabled) {
    return (
      <div
        aria-disabled="true"
        title={item.disabledTitle}
        style={{
          ...rowStyle({ active: false, depth }),
          color: COLORS.outline,
          cursor: "default",
        }}
      >
        <Icon name={item.icon} size={18} />
        <span style={{ flex: 1 }}>{item.label}</span>
      </div>
    );
  }

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          style={rowStyle({ active: directActive, depth })}
        >
          {directActive && <ActiveStripe />}
          <Icon name={item.icon} size={18} />
          <span style={{ flex: 1 }}>{item.label}</span>
          <Icon name={open ? "chevron-down" : "chevron-right"} size={18} />
        </button>
        {open && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
            {item.children!.map((child) => (
              <SidebarRow
                key={child.id}
                item={child}
                pathname={pathname}
                depth={depth + 1}
                onClick={onClick}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <a
      href={item.href ?? "#"}
      aria-current={directActive ? "page" : undefined}
      onClick={(e) => {
        if (onNavigate && item.href && !item.external) {
          e.preventDefault()
          onNavigate(item.href)
        }
        onClick?.()
      }}
      style={rowStyle({ active: directActive, depth })}
    >
      {directActive && <ActiveStripe />}
      <Icon name={item.icon} size={18} />
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge != null && <Badge value={item.badge} active={directActive} id={`sidebar:${item.id}`} />}
    </a>
  );
}

export interface SidebarLabels {
  closeMenu?: string
  signOut?: string
}

export interface SidebarProps {
  open: boolean;
  onClose: () => void;
  currentPath: string;
  navSections?: NavSection[];
  onSignOut?: () => void;
  labels?: SidebarLabels;
  slotBottom?: ReactNode;
  onNavigate?: (href: string) => void;
}

export function Sidebar({ open, onClose, currentPath, navSections = DEFAULT_NAV_SECTIONS, onSignOut, labels, slotBottom, onNavigate }: SidebarProps) {
  const closeMenuLabel = labels?.closeMenu ?? 'Close menu'
  const signOutLabel = labels?.signOut ?? 'Sign out'
  const pathname = currentPath;

  return (
    <aside
      className={`wh-sidebar${open ? " open" : ""}`}
      style={{
        width: 248,
        flexShrink: 0,
        alignSelf: "flex-start",
        background: COLORS.primaryContainer,
        borderRadius: 8,
        padding: "16px 12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="wh-sidebar-close-btn">
        <button
          onClick={onClose}
          aria-label={closeMenuLabel}
          style={{
            width: 36, height: 36, borderRadius: 8,
            background: "transparent", border: "none",
            color: COLORS.onSurface, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
        {navSections.map((section, i) => (
          <div key={section.label ?? `section-${i}`}>
            {section.label && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: COLORS.outline,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  padding: "4px 12px 8px",
                }}
              >
                {section.label}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {section.items.map((item) => (
                <SidebarRow key={item.id} item={item} pathname={pathname} onClick={onClose} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div style={{ paddingTop: 16, borderTop: `1px solid ${COLORS.onSurfaceOpacity8}`, marginTop: 16, display: "flex", flexDirection: "column", gap: 2 }}>
        <ThemeToggle />
        {slotBottom}
        <a
          href="/sign-out"
          onClick={(e) => { if (onSignOut) { e.preventDefault(); onSignOut(); } onClose(); }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            height: 40,
            padding: "0 12px",
            borderRadius: 6,
            color: COLORS.onSurface,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <Icon name="logout" size={18} />
          <span>{signOutLabel}</span>
        </a>
      </div>
    </aside>
  );
}
